import { Injectable } from "@angular/core";
import { ETaskPrefix } from "@calaosoft/osapp-common/background-tasks/models/ETaskPrefix";
import { ConfigData } from "@calaosoft/osapp-common/config/models/ConfigData";
import { Entity } from "@calaosoft/osapp-common/entities/models/entity";
import { GuidHelper } from "@calaosoft/osapp-common/guid/helpers/guidHelper";
import { EDatabaseRole } from "@calaosoft/osapp-common/store/models/edatabase-role";
import { StringHelper } from "@calaosoft/osapp-common/utils/helpers/stringHelper";
import { EPrefix } from "@calaosoft/osapp-common/utils/models/EPrefix";
import { ESuffix } from "@calaosoft/osapp-common/utils/models/ESuffix";
import { firstValueFrom, Subject, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { EApplicationEventType } from "../model/application/EApplicationEventType";
import { IApplicationEvent } from "../model/application/IApplicationEvent";
import { IConvSyncTaskParams } from "../model/backgroundTask/taskParams/IConvSyncTaskParams";
import { IDmsTaskParams } from "../model/backgroundTask/taskParams/idms-task-params";
import { IFlushLogTaskParams } from "../model/backgroundTask/taskParams/IFlushLogTaskParams";
import { IHeartbeatTaskParams } from "../model/backgroundTask/taskParams/IHeartbeatTaskParams";
import { IStoreTaskParams } from "../model/backgroundTask/taskParams/IStoreTaskParams";
import { ITaskParams } from "../model/backgroundTask/taskParams/ITaskParams";
import { IUpdateTaskParams } from "../model/backgroundTask/taskParams/IUpdateTaskParams";
import { EConfigFlag } from "../model/config/econfig-flag";
import { EEventTypeConfig } from "../model/config/eevent-type-config";
import { IEventConfig } from "../model/config/ievent-config";
import { EConversationEvent } from "../model/conversation/EConversationEvent";
import { IConversationEvent } from "../model/conversation/IConversationEvent";
import { IDatabaseDocument } from "../model/databaseDocument/IDatabaseDocument";
import { IDatabaseDocumentEvent } from "../model/databaseDocument/IDatabaseDocumentEvent";
import { IFlag } from "../model/flag/IFlag";
import { IFormEvent } from "../model/forms/IFormEvent";
import { ENotificationEventType } from "../model/notification/ENotificationEventType";
import { ENotificationFlag } from "../model/notification/ENotificationFlag";
import { INotificationEvent } from "../model/notification/INotificationEvent";
import { ESecurityFlag } from "../model/security/ESecurityFlag";
import { ISecurityEvent } from "../model/security/ISecurityEvent";
import { Database } from "../model/store/Database";
import { EStoreEventStatus } from "../model/store/EStoreEventStatus";
import { EStoreEventType } from "../model/store/EStoreEventType";
import { EStoreFlag } from "../model/store/EStoreFlag";
import { IStoreEvent } from "../model/store/IStoreEvent";
import { StoreTaskDescriptorHelper } from "../modules/bts/helpers/store-task-descriptor.helper";
import { ITaskDescriptor } from "../modules/bts/models/itask-descriptor";
import { TaskDescriptor } from "../modules/bts/models/task-descriptor";
import { EDmsAction } from "../modules/dms/model/EDmsAction";
import { EDmsFlag } from "../modules/dms/model/EDmsFlag";
import { IDmsEvent } from "../modules/dms/model/idms-event";
import { LocalDmsService } from "../modules/dms/services/localDms.service";
import { RemoteDmsService } from "../modules/dms/services/remoteDms.service";
import { SyncDmsService } from "../modules/dms/services/syncDms.service";
import { EntitiesService } from "../modules/entities/services/entities.service";
import { LoggerService } from "../modules/logger/services/logger.service";
import { ENetworkFlag } from "../modules/network/models/enetwork-flag";
import { IPermissionEvent } from "../modules/permissions/models/IPermissionEvent";
import { PermissionsService } from "../modules/permissions/services/permissions.service";
import { ApplicationService } from "./application.service";
import { BackgroundTaskService } from "./backgroundTask.service";
import { ConfigService } from "./config.service";
import { ContactsService } from "./contacts.service";
import { ConversationService } from "./conversation.service";
import { DatabaseDocumentInitializerService } from "./databaseDocumentInitializer.service";
import { FlagService } from "./flag.service";
import { FormsService } from "./forms.service";
import { NotificationService } from "./notification.service";
import { SecurityService } from "./security.service";
import { Store } from "./store.service";
import { UpdateService } from "./update.service";
import { WorkspaceService } from "./workspace.service";

@Injectable({ providedIn: "root" })
export class OrchestratorService {
	//#region FIELDS

	private static readonly C_LOG_ID = "ORCH.S::";

	/** Sujet de l'application permettant de faire transiter les événements de l'application. */
	private readonly moApplicationSubject: Subject<IApplicationEvent> = new Subject();

	//#endregion

	//#region METHODS

	constructor(
		private readonly isvcBackgroundTask: BackgroundTaskService,
		private readonly isvcFlag: FlagService,
		private readonly isvcWorkspace: WorkspaceService,
		private readonly isvcStore: Store,
		private readonly isvcConfig: ConfigService,
		private readonly isvcLocalDms: LocalDmsService,
		private readonly isvcRemoteDms: RemoteDmsService,
		private readonly isvcSyncDms: SyncDmsService,
		private readonly isvcSecurity: SecurityService,
		private readonly isvcConversation: ConversationService,
		private readonly isvcForms: FormsService,
		private readonly isvcApplication: ApplicationService,
		private readonly isvcDatabaseDocInit: DatabaseDocumentInitializerService,
		private readonly isvcUpdate: UpdateService,
		private readonly isvcPermissions: PermissionsService,
		private readonly isvcNotification: NotificationService,
		private readonly isvcContact: ContactsService,
		private readonly isvcEntities: EntitiesService
	) {}

	public init(): void {
		this.isvcApplication.setApplicationSubject(this.moApplicationSubject);
		this.initSubscribtions();
		this.addFlushLogTask();
	}

	/** Ajoute les flags aux événements de config.
	 * @param poEvent Événément reçu.
	 */
	private addConfigFlags(poEvent: IEventConfig): void {
		if (!poEvent.alteredFlags) poEvent.alteredFlags = [];

		switch (poEvent.data.configEventType) {
			case EEventTypeConfig.PlatformReady:
				poEvent.alteredFlags = [{ key: EConfigFlag.PlatformReady, value: true }];
				break;

			case EEventTypeConfig.ConfigReady:
				poEvent.alteredFlags = [{ key: EConfigFlag.ConfigReady, value: true }];
				break;

			case EEventTypeConfig.StaticConfigReady:
				poEvent.alteredFlags = [{ key: EConfigFlag.StaticConfigReady, value: true }];
				break;
		}
	}

	/** Ajoute les flags aux événements de notifications.
	 * @param poEvent Événément reçu.
	 */
	private addNotificationFlags(poEvent: INotificationEvent): void {
		if (!poEvent.alteredFlags) poEvent.alteredFlags = [];

		switch (poEvent.data.notificationEventType) {
			case ENotificationEventType.Initialised:
				poEvent.alteredFlags = [{ key: ENotificationFlag.Initialized, value: true }];
				break;
		}
	}

	/** Ajoute les flags aux événements du dms local.
	 * @param poEvent Événement reçu.
	 */
	private addLocalDmsFlags(poEvent: IDmsEvent): void {
		if (poEvent.data.isDmsReady) poEvent.alteredFlags = [{ key: EDmsFlag.DmsReady, value: true }];
	}

	/** Ajoute les flags à l'événement.
	 * @param poEvent Événement reçu.
	 */
	private addSecurityFlags(poEvent: ISecurityEvent): void {
		if (!poEvent.alteredFlags) poEvent.alteredFlags = [];
		if (typeof poEvent.data.isAuthenticated === "boolean")
			poEvent.alteredFlags.push({ key: ESecurityFlag.authenticated, value: poEvent.data.isAuthenticated });
	}

	/** Ajoute les flags à modifier à l'événement du store.
	 * @param poEvent Événement à modifier.
	 */
	private addStoreFlags(poEvent: IStoreEvent): void {
		poEvent.alteredFlags = [];

		switch (poEvent.data.storeEventType) {
			case EStoreEventType.Init:
				if (!poEvent.data.databaseId && poEvent.data.status === EStoreEventStatus.successed)
					poEvent.alteredFlags.push({ key: EStoreFlag.DBInitialized, value: true });
				else if (!poEvent.data.databaseId && poEvent.data.status === EStoreEventStatus.failed)
					poEvent.alteredFlags.push({ key: EStoreFlag.DBInitialized, value: false });
				break;

			case EStoreEventType.Synchro:
				if (poEvent.data.status === EStoreEventStatus.working)
					poEvent.alteredFlags.push({ key: EStoreFlag.DBIsSyncing, value: true });
				break;

			case EStoreEventType.InitStartup:
				if (!poEvent.data.databaseId && poEvent.data.status === EStoreEventStatus.successed)
					poEvent.alteredFlags.push({ key: EStoreFlag.DBStartupInitialized, value: true });
				else if (!poEvent.data.databaseId && poEvent.data.status === EStoreEventStatus.failed)
					poEvent.alteredFlags.push({ key: EStoreFlag.DBStartupInitialized, value: false });
				break;
		}
	}

	/** Initialise tous les abonnements. */
	private initSubscribtions(): void {
		this.isvcStore.subscribe((poEvent: IStoreEvent) => this.onStoreEventAsync(poEvent));
		this.isvcConfig.subscribe((poEvent: IEventConfig) => this.onConfigEventAsync(poEvent));
		this.isvcNotification.subscribe((poEvent: INotificationEvent) => this.onNotificationEventAsync(poEvent));
		this.isvcLocalDms.subscribe((poEvent: IDmsEvent) => this.onLocalDmsEvent(poEvent));
		this.isvcRemoteDms.subscribe((poEvent: IDmsEvent) => this.onRemoteDmsEvent(poEvent));
		this.isvcSyncDms.subscribe((poEvent: IDmsEvent) => this.onSyncDmsEventAsync(poEvent));
		this.isvcSecurity.subscribe((poEvent: ISecurityEvent) => this.onSecurityEventAsync(poEvent));
		this.isvcConversation.subscribe((poEvent: IConversationEvent) => this.onConversationEvent(poEvent));
		this.isvcApplication.subscribe((poEvent: IApplicationEvent) => this.onApplicationEvent(poEvent));
		this.isvcForms.getFormEventObservable().subscribe((poEvent: IFormEvent) => this.onFormsEvent(poEvent));
		this.isvcPermissions.permissionsEvent$.subscribe((poEvent: IPermissionEvent) => this.onPermissionEvent(poEvent));
		this.isvcContact.events$.subscribe((poEvent: IApplicationEvent) => this.onContactsEvent(poEvent));
	}

	private onPermissionEvent(poEvent: IPermissionEvent): void {
		this.setFlags(poEvent);
		this.raiseEvent(poEvent);
	}

	private onContactsEvent(poEvent: IApplicationEvent): void {
		this.setFlags(poEvent);
		this.raiseEvent(poEvent);
	}

	/** Ajoute une tache quand l'évenement est levé. */
	private addFlushLogTask(): void {
		this.isvcBackgroundTask
			.addTask$<IFlushLogTaskParams>({
				taskId: `${ETaskPrefix.flushLog}task`,
				description: "Envoi des logs sur le serveur",
				taskType: "FlushLogTask",
				completionInterval: LoggerService.C_FLUSH_LOG_INTERVAL_MS,
				execAfter: [
					{ key: ENetworkFlag.isNetworkReliable, value: true },
					{ key: EStoreFlag.DBInitialized, value: true }
				]
			})
			.subscribe();
	}

	/** Lève un événement après l'avoir traité.
	 * @param poEvent Événement traité qu'il faut renvoyer.
	 */
	private raiseEvent(poEvent: IApplicationEvent): void {
		this.moApplicationSubject.next(poEvent);
	}

	/** Nouvel événement provenant du service de l'application.
	 * @param poEvent Événement d'application survenu.
	 */
	private onApplicationEvent(poEvent: IApplicationEvent): void {
		switch (poEvent.type) {
			case EApplicationEventType.databaseDocumentEvent:
				const loDatabaseDocument: IDatabaseDocument = {
					document: (poEvent as IDatabaseDocumentEvent).data.document,
					databaseIds: this.isvcStore.getDatabasesIdsByRole((poEvent as IDatabaseDocumentEvent).data.databasesRole)
				};
				this.isvcDatabaseDocInit.addDocument(loDatabaseDocument);
				break;
		}
	}

	private onFormsEvent(poEvent: IFormEvent): void {
		this.raiseEvent(poEvent);
	}

	/** Méthode appelée lors de l'arrivée d'un flag de conversation*/
	private onConversationEvent(poEvent: IConversationEvent): void {
		if (poEvent.data.eventType === EConversationEvent.synchro) this.synchroConvDb();
	}

	/** Lance la synchro d'une base locale vers une base distante.
	 * @param poEvent Événement qui lance la synchro.
	 */
	private synchroConvDb(): void {
		this.isvcBackgroundTask
			.addTask$<IConvSyncTaskParams>({
				taskId: `${ETaskPrefix.dbSync}conv`,
				description: "Synchronisation local-serveur des conversations",
				taskType: "ConvSyncTask",
				params: {},
				enableTaskPersistance: true,
				isCancellable: false,
				execAfter: [
					{ key: ENetworkFlag.isNetworkReliable, value: true },
					{ key: EStoreFlag.DBInitialized, value: true }
				],
				retryAfter: [
					{ key: ENetworkFlag.isNetworkReliable, value: true },
					{ key: EStoreFlag.DBInitialized, value: true }
				]
			})
			.subscribe();
	}

	/** Appelée lors de l'arrivée d'un événement du config service.
	 * @param poEvent Événement reçu.
	 */
	private async onConfigEventAsync(poEvent: IEventConfig): Promise<void> {
		// La plateforme est prête.
		this.addConfigFlags(poEvent);
		this.setFlags(poEvent);
		this.raiseEvent(poEvent);

		switch (poEvent.data.configEventType) {
			case EEventTypeConfig.StaticConfigReady:
				if (this.isvcUpdate.getPlatformUpdateConfig() && !ConfigData.environment.storeRelease) {
					// Regarde si une mise à jour est disponible.
					await firstValueFrom(
						this.isvcBackgroundTask.addTask$<IUpdateTaskParams>({
							taskId: "upd_check",
							description: "Mise à jour de l'application",
							taskType: "UpdateTask",
							completionInterval: 1800000,
							intervalMultiplicator: 1,
							execAfter: [
								{ key: ENetworkFlag.isNetworkReliable, value: true },
								{ key: ESecurityFlag.authenticated, value: true }
							]
						})
					);
				}

				// Initialise le composant de notifications.
				await firstValueFrom(
					this.isvcBackgroundTask.addTask$({
						taskId: "notif_init",
						description: "Initialisation du service de notification",
						taskType: "NotifInitTask"
					})
				);
				break;
		}
	}

	/** Appelée lors de l'arrivée d'un événement du notification service.
	 * @param poEvent Événement reçu.
	 */
	private async onNotificationEventAsync(poEvent: INotificationEvent): Promise<void> {
		this.addNotificationFlags(poEvent);
		this.setFlags(poEvent);
		this.raiseEvent(poEvent);
	}

	/** Appelée lors de l'arrivée d'un événement du dms local.
	 * @param poEvent Événement reçu.
	 */
	private onLocalDmsEvent(poEvent: IDmsEvent): void {
		// Le dms local est prêt.
		this.addLocalDmsFlags(poEvent);
		this.setFlags(poEvent);
		this.raiseEvent(poEvent);
	}

	/** Appelée lors de l'arrivée d'un événement du dms distant.
	 * @param poEvent Événement reçu.
	 */
	private onRemoteDmsEvent(poEvent: IDmsEvent): void {
		this.raiseEvent(poEvent);
	}

	/** Lancée lors de la réception d'un événement du security service.
	 * @param poEvent Événement reçu.
	 */
	private async onSecurityEventAsync(poEvent: ISecurityEvent): Promise<void> {
		await this.addSecurityTasksAsync(poEvent);
		this.addSecurityFlags(poEvent);
		this.setFlags(poEvent);
		this.raiseEvent(poEvent);
	}

	/** Ajoute les tâches demandés par le service de sécurité.
	 * @param poEvent Événement du service de sécurité.
	 */
	private async addSecurityTasksAsync(poEvent: ISecurityEvent): Promise<void> {
		if (poEvent.data.startHeartbeat) {
			await firstValueFrom(
				this.isvcBackgroundTask.addTask$<IHeartbeatTaskParams>({
					taskId: `${ETaskPrefix.heartbeat}task`,
					description: "Heartbeat",
					taskType: "HeartbeatTask",
					execAfter: [{ key: ESecurityFlag.authenticated, value: true }],
					beginningInterval: ConfigData.authentication.hearbeatIntervalMilliseconds
				})
			);
		}
	}

	/** Appelée lors de l'arrivée d'un événement du store.
	 * @param poEvent Événement reçu.
	 */
	private async onStoreEventAsync(poEvent: IStoreEvent): Promise<void> {
		this.addStoreFlags(poEvent);
		this.setFlags(poEvent);

		switch (poEvent.data.storeEventType) {
			case EStoreEventType.Synchro:
				if (!StringHelper.isBlank(poEvent.data.databaseId) && poEvent.data.status === EStoreEventStatus.required)
					await this.addSynchroDbTaskAsync(poEvent.data.databaseId);
				else this.raiseEvent(poEvent);
				break;

			case EStoreEventType.NeedInfos:
				const leRole: EDatabaseRole =
					poEvent.data.document instanceof Entity
						? this.isvcEntities.getEntityRole(poEvent.data.document)
						: EDatabaseRole.workspace;

				this.isvcWorkspace
					.requestWorkspaceSelection(leRole)
					.pipe(
						catchError((poError) => {
							poEvent.data.error = poError;
							this.raiseEvent(poEvent);
							return throwError(() => poError);
						}),
						tap((psResult: string) => {
							if (!StringHelper.isBlank(psResult))
								// Dans le cas où on veut annuler, le résultat est à `undefined`.
								poEvent.data.databaseId = psResult;
							this.raiseEvent(poEvent);
						})
					)
					.subscribe();
				break;

			case EStoreEventType.Init:
				if (!StringHelper.isBlank(poEvent.data.databaseId) && poEvent.data.status === EStoreEventStatus.needReplication)
					await this.addReplicateDatabaseTaskAsync(poEvent.data.databaseId, poEvent.data.login, poEvent.data.password);
				else if (
					!StringHelper.isBlank(poEvent.data.databaseId) &&
					poEvent.data.status === EStoreEventStatus.initToLocalReplication
				)
					await this.addReplicateToLocalDatabaseTaskAsync(poEvent.data.databaseId);
				else this.raiseEvent(poEvent);
				break;

			case EStoreEventType.OptimizationRefused:
				if (!StringHelper.isBlank(poEvent.data.databaseId))
					await this.onOptimizationRefusedAsync(poEvent.data.databaseId, poEvent.data.prebuildSeq ?? 0);
				break;

			default:
				this.raiseEvent(poEvent);
				break;
		}
	}

	/** Appelée lors de l'arrivée d'un événement du dms synchro.
	 * @param poEvent Événement reçu.
	 */
	private async onSyncDmsEventAsync(poEvent: IDmsEvent): Promise<void> {
		if (poEvent.data?.dmsAction) {
			let loTaskDescriptor: Omit<ITaskDescriptor<IDmsTaskParams>, "uid">;

			switch (poEvent.data.dmsAction) {
				case EDmsAction.download:
					loTaskDescriptor = this.createDmsTask(
						ESuffix.download,
						`Téléchargement fichier '${poEvent.data.fileId}'`,
						"DownloadDmsFileTask",
						poEvent.data.fileId
					);
					break;

				case EDmsAction.sync:
					loTaskDescriptor = this.createDmsTask(
						ESuffix.all_pending,
						"Synchronisation des fichiers DMS en attente",
						"SyncDmsTask",
						undefined,
						undefined,
						poEvent.data.excludedPrefixes
					);
					break;

				case EDmsAction.upload:
					// Pas besoin de persister la tâche, le DmsService gère les `pen_ul` au démarrage de l'app.
					loTaskDescriptor = this.createDmsTask(
						ESuffix.upload,
						`Téléversement fichier '${poEvent.data.fileId}'`,
						"UploadDmsFileTask",
						poEvent.data.fileId,
						9999 // On met un nombre de retry très élevé pour être sûr que le fichier soit téléversé, même en cas de problème récurrent.
					);
					break;

				case EDmsAction.delete:
					loTaskDescriptor = this.createDmsTask(
						ESuffix.delete,
						`Suppression fichier '${poEvent.data.fileId}'`,
						"DeleteDmsFileTask",
						poEvent.data.fileId
					);
					break;
			}

			await firstValueFrom(this.isvcBackgroundTask.addTask$(loTaskDescriptor));
		} else this.raiseEvent(poEvent);
	}

	private createDmsTask(
		peTaskSuffix: ESuffix,
		psTaskDescription: string,
		psTaskType: string,
		psFileId?: string,
		pnMaxRetry?: number,
		paExcludedPrefixes?: Array<EPrefix.pendingDownload | EPrefix.pendingDelete | EPrefix.pendingUpload>
	): Omit<ITaskDescriptor<IDmsTaskParams>, "uid"> {
		const lsFileId: string = StringHelper.isBlank(psFileId) ? "" : psFileId;
		const lsFileGuid: string = GuidHelper.extractGuid(psFileId);
		const loTaskParams: IDmsTaskParams = {
			fileGuid: StringHelper.isBlank(lsFileGuid) ? "" : lsFileGuid,
			excludedPrefixes: paExcludedPrefixes
		};

		return {
			taskId: `${ETaskPrefix.dmsSync}${lsFileId}${peTaskSuffix}`,
			description: psTaskDescription,
			taskType: psTaskType,
			params: loTaskParams,
			maxRetry: pnMaxRetry,
			execAfter: [
				{ key: ENetworkFlag.isNetworkReliable, value: true },
				{ key: EStoreFlag.DBInitialized, value: true }
			],
			retryAfter: [
				{ key: ENetworkFlag.isNetworkReliable, value: true },
				{ key: EStoreFlag.DBInitialized, value: true }
			]
		};
	}

	/** Permet de modifier les flags qui sont spécifiés dans l'événement.
	 * @param poEvent Événement reçu.
	 */
	private setFlags(poEvent: IApplicationEvent): void {
		if (poEvent.alteredFlags)
			poEvent.alteredFlags.forEach((poFlag: IFlag) => this.isvcFlag.setFlagValue(poFlag.key, poFlag.value));
	}

	/** Lance la synchro d'une base locale vers une base distante.
	 * @param poEvent Événement qui lance la synchro.
	 */
	private async addSynchroDbTaskAsync(psDbId: string): Promise<void> {
		if (StringHelper.isValid(psDbId)) {
			await firstValueFrom(
				this.isvcBackgroundTask.addTask$(StoreTaskDescriptorHelper.createLocalToServerTaskDescriptor(psDbId))
			);
		} else console.error(`${OrchestratorService.C_LOG_ID}Can not synchronize database because id is "${psDbId}".`);
	}

	/** Lance la réplication d'une base de données.
	 * @param poEvent Événement qui lance l'initialisation.
	 */
	private async addReplicateDatabaseTaskAsync(psDbId: string, psLogin?: string, psPassword?: string): Promise<void> {
		if (StringHelper.isValid(psDbId)) {
			const loTaskParams: IStoreTaskParams = {
				dbId: psDbId,
				login: psLogin,
				password: psPassword
			};
			const laFlagsToWait: IFlag[] = [{ key: ENetworkFlag.isNetworkReliable, value: true }];

			if (psDbId !== ConfigData.environment.coreRoleAppConfig)
				laFlagsToWait.push({ key: ESecurityFlag.authenticated, value: true });

			await firstValueFrom(
				this.isvcBackgroundTask.addTask$({
					taskId: `${ETaskPrefix.dbInit}${psDbId}`,
					description: "Réplication d'une base de données 'replicateOnStart'",
					taskType: "DatabaseReplicateTask",
					params: loTaskParams,
					execAfter: laFlagsToWait,
					retryAfter: laFlagsToWait
				})
			);
		} else console.error(`${OrchestratorService.C_LOG_ID}Can not synchronize database because id is "${psDbId}".`);
	}

	/** Traite le refus de l'optimisation par prebuild : supprime la tâche de réplication descendante de la base
	 * (sinon la popup serait reproposée à chaque cycle de réplication) et planifie une tâche de rappel non sérialisée.
	 * @param psDbId Identifiant de la base de données concernée.
	 * @param pnPrebuildSeq Numéro de séquence du prebuild proposé.
	 */
	private async onOptimizationRefusedAsync(psDbId: string, pnPrebuildSeq: number): Promise<void> {
		const [loRemoveResult, loAddResult]: [
			PromiseSettledResult<TaskDescriptor<ITaskParams> | undefined>,
			PromiseSettledResult<boolean>
		] = await Promise.allSettled([
			firstValueFrom(
				this.isvcBackgroundTask.removeTaskById$(StoreTaskDescriptorHelper.getServerToLocalReplicationTaskId(psDbId))
			),
			firstValueFrom(
				this.isvcBackgroundTask.addTask$(
					StoreTaskDescriptorHelper.createOptimizationReminderTaskDescriptor(psDbId, pnPrebuildSeq)
				)
			)
		]);

		if (loRemoveResult.status === "rejected") {
			console.error(
				`${OrchestratorService.C_LOG_ID}Failed to remove server-to-local replication task for '${psDbId}'.`,
				loRemoveResult.reason
			);
		}
		if (loAddResult.status === "rejected") {
			console.error(
				`${OrchestratorService.C_LOG_ID}Failed to add optimization reminder task for '${psDbId}'.`,
				loAddResult.reason
			);
		}
	}

	/** Lance la réplication serveur vers local d'une base de données.
	 * @param poEvent Événement qui lance l'initialisation.
	 */
	private async addReplicateToLocalDatabaseTaskAsync(psDbId: string): Promise<void> {
		if (StringHelper.isValid(psDbId)) {
			const loDatabase: Database = this.isvcStore.getDatabaseById(psDbId);
			await firstValueFrom(
				this.isvcBackgroundTask.addTask$(StoreTaskDescriptorHelper.createServerToLocalTaskDescriptor(loDatabase))
			);
		} else console.error(`${OrchestratorService.C_LOG_ID}Can not download server database because id not defined.`);
	}

	//#endregion
}
